var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { getSearchProvider } from './providers';
export function useSearchProvider(providerType, mockPlatform) {
    // Detect if we're on a search page - start with loading true to show skeleton
    const isSearchPage = typeof window !== 'undefined' && window.location.pathname.includes('/search');
    // Core search state
    const [loading, setLoading] = useState(isSearchPage);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [meta, setMeta] = useState(null);
    const [sortOptions, setSortOptions] = useState([]);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    const latestRequestId = useRef(0);
    // Search parameters state
    const [searchState, setSearchState] = useState({
        query: '',
        currentSort: 'manual',
        appliedFilters: {}
    });
    // URL parameter management
    const updateUrlParams = useCallback(({ query, currentSort, appliedFilters }) => {
        const urlParams = new URLSearchParams();
        if (currentSort) {
            urlParams.set('sort', currentSort);
        }
        if (query) {
            urlParams.set('q', query);
        }
        Object.entries(appliedFilters).forEach(([key, values]) => {
            values.forEach(value => {
                urlParams.append(key, value);
            });
        });
        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    }, []);
    // Initialize from URL parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get('q');
        const sortParam = urlParams.get('sort');
        const initialFilters = {};
        urlParams.forEach((value, key) => {
            if (key !== 'q' && key !== 'sort') {
                if (!initialFilters[key]) {
                    initialFilters[key] = [];
                }
                initialFilters[key].push(value);
            }
        });
        const newSearchState = {
            query: queryParam || '',
            currentSort: sortParam || '',
            appliedFilters: initialFilters
        };
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(newSearchState.query, {
            sort: newSearchState.currentSort || undefined,
            filters: newSearchState.appliedFilters
        });
    }, []);
    // Core search function
    const runSearch = (query_1, ...args_1) => __awaiter(this, [query_1, ...args_1], void 0, function* (query, options = {}) {
        const requestId = ++latestRequestId.current;
        setLoading(true);
        try {
            yield provider.search(query, options);
            // Only the newest overlapping search may update the UI.
            if (requestId !== latestRequestId.current) {
                return;
            }
            setProducts(provider.getProducts());
            setFilters(provider.getFilters());
            setMeta(provider.getMeta());
            setSortOptions(provider.getSortOptions());
        }
        catch (error) {
            if (requestId === latestRequestId.current) {
                console.error('Search failed:', error);
            }
        }
        finally {
            if (requestId === latestRequestId.current) {
                setLoading(false);
            }
        }
    });
    // Event handlers
    const handleQuerySubmit = (newQuery) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { query: newQuery });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(newQuery, {
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters
        });
    };
    const handleFilterChange = (filterId, value) => {
        setSearchState(prev => {
            var _a;
            const newFilters = Object.assign({}, prev.appliedFilters);
            if (filterId.includes('price')) {
                newFilters[filterId] = [value];
            }
            else {
                if ((_a = newFilters[filterId]) === null || _a === void 0 ? void 0 : _a.includes(value)) {
                    newFilters[filterId] = newFilters[filterId].filter(v => v !== value);
                    if (newFilters[filterId].length === 0) {
                        delete newFilters[filterId];
                    }
                }
                else {
                    newFilters[filterId] = [...(newFilters[filterId] || []), value];
                }
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters });
            updateUrlParams(newSearchState);
            runSearch(prev.query, {
                sort: prev.currentSort || undefined,
                filters: newFilters
            });
            return newSearchState;
        });
    };
    const handlePageChange = (page) => {
        runSearch(searchState.query, {
            page,
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters
        });
    };
    const handleSortChange = (sort) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { currentSort: sort });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            sort: sort || undefined,
            filters: searchState.appliedFilters
        });
    };
    return {
        loading,
        products,
        filters,
        meta,
        sortOptions,
        query: searchState.query,
        currentSort: searchState.currentSort,
        appliedFilters: searchState.appliedFilters,
        handleQuerySubmit,
        handleFilterChange,
        handlePageChange,
        handleSortChange,
        runSearch
    };
}
